import {
    Text,
    DraggableList,
    type DraggableItem,
    Card,
    Icon,
    ActionMenu,
    Colors,
    Pills,
} from '@amelio/ui-library';

import type { QuestionActionItemsProps } from '../../types';
import type { User } from '@/types/directory/User';
import type { ReactNode } from 'react';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';
import {
    ReviewQuestionStatus,
    type ReviewQuestionStatusType,
} from '@/constants/questionnaire/Question';
import {
    ManagerAssessmentSkillSelectorOption,
    type ManagerAssessmentSkillSelectorOptionValue,
} from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { ManagerSkillSelectorTile } from '@/pages/ManageTemplates/TemplateQuestionnaire/components/ManagerSkillSelectorTile';
import { type ReviewQuestion } from '@/types/review/ReviewQuestion';
import { getTextByLanguage } from '@/utils/localization';

interface QuestionListProps {
    questions: ReviewQuestion[];
    actionItems: QuestionActionItemsProps[];
    isPendingDeleteQuestion: boolean;
    isCompetency?: boolean;
    onReorder: (items: DraggableItem<ReviewQuestion>[]) => void;
    isTemplateCreationPage?: boolean;
    onStatusToggle?: (question: ReviewQuestion, newStatus: ReviewQuestionStatusType) => void;
    onRowClick?: (question: ReviewQuestion) => void;
    editorSlotAfterId?: string | null;
    renderEditorSlot?: () => ReactNode;
    hiddenQuestionId?: string;
    previewEvaluee?: User;
    skillSelectorOption?: ManagerAssessmentSkillSelectorOptionValue;
}

export const QuestionList = ({
    questions,
    actionItems,
    onReorder,
    onStatusToggle,
    onRowClick,
    editorSlotAfterId,
    renderEditorSlot,
    hiddenQuestionId,
    previewEvaluee,
    isCompetency = false,
    skillSelectorOption,
}: QuestionListProps) => {
    const companyLanguage = useSelectedAppLanguage();
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const hasEditorSlot = editorSlotAfterId !== undefined && renderEditorSlot !== undefined;
    const showSkillSelectorTile =
        skillSelectorOption !== undefined &&
        skillSelectorOption !== ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS;

    if (questions.length === 0 && !hasEditorSlot && !showSkillSelectorTile) {
        return null;
    }

    const draggableItems: DraggableItem<ReviewQuestion>[] = questions.map((question, index) => ({
        id: question.id || `temp-${index}`,
        data: question,
    }));

    const handleReorder = (reorderedItems: DraggableItem<ReviewQuestion>[]) => {
        const reorderedQuestions = reorderedItems.map((item) => ({
            id: item.id,
            data: item.data,
        }));
        onReorder(reorderedQuestions);
    };

    const handleStatusToggle = (question: ReviewQuestion) => {
        if (!onStatusToggle) return;
        const newStatus: ReviewQuestionStatusType =
            question.status === ReviewQuestionStatus.ACTIVE
                ? ReviewQuestionStatus.INACTIVE
                : ReviewQuestionStatus.ACTIVE;
        onStatusToggle(question, newStatus);
    };

    const getActionMenuItems = (question: ReviewQuestion) => {
        const items = actionItems.map((item) => ({
            ...item,
            onClick: () => item.onClick(question),
        }));

        if (onStatusToggle) {
            const isActive = question.status === ReviewQuestionStatus.ACTIVE;
            const statusAction = {
                label: isActive ? t('common.deactivate') : t('common.activate'),
                onClick: () => handleStatusToggle(question),
                icon: (
                    <Icon
                        name={isActive ? 'electronics-power-off' : 'electronics-power-on'}
                        size={'sm'}
                        color={isActive ? Colors.ERROR : Colors.SUCCESS}
                    />
                ),
            };
            // Insert before the last item (usually Delete) or at the end
            items.splice(Math.max(0, items.length - 1), 0, statusAction);
        }

        return items;
    };

    const stopTapPropagation = (event: React.SyntheticEvent) => {
        event.stopPropagation();
    };

    const disabledItemIds = hiddenQuestionId ? [hiddenQuestionId] : undefined;

    return (
        <div className={'flex w-full flex-col gap-4'}>
            {showSkillSelectorTile && (
                <ManagerSkillSelectorTile selectedOption={skillSelectorOption} />
            )}
            <DraggableList
                items={draggableItems}
                onReorder={handleReorder}
                onItemTap={onRowClick ? (item) => onRowClick(item.data) : undefined}
                editorSlotAfterId={editorSlotAfterId}
                renderEditorSlot={renderEditorSlot}
                disabledItemIds={disabledItemIds}
                gap={isCompetency ? 20 : 10}
                renderItem={(question) => {
                    const isHidden =
                        hiddenQuestionId !== undefined && question.id === hiddenQuestionId;
                    if (isHidden) {
                        return (
                            <div
                                data-testid={`question-row-hidden-${question.id}`}
                                aria-hidden={true}
                                className={'h-0 w-full overflow-hidden'}
                            />
                        );
                    }
                    return (
                        <Card
                            className={
                                'w-full flex-row items-center transition-all hover:cursor-grab hover:shadow-md'
                            }
                            borderRadius={'lg'}
                            padding={'sm'}
                            bgColor={isCompetency ? 'bg-base-50' : 'bg-white/70'}
                        >
                            {/* Handle */}
                            <div
                                className={
                                    'border-base-100 relative -left-7 flex items-center justify-center rounded-lg border bg-white p-2'
                                }
                            >
                                <Icon
                                    name={'settings-menu'}
                                    size={'xs'}
                                />
                            </div>

                            {/* Content */}
                            <div className={'-ml-5 flex w-full items-start justify-between gap-4'}>
                                <div className={'flex flex-1 items-center justify-center gap-3'}>
                                    <div className={'flex-1'}>
                                        <Text
                                            weight={'medium'}
                                            className={'text-base-600'}
                                        >
                                            <QuestionTextRenderer
                                                text={replaceDynamicTags({
                                                    text: getTextByLanguage(
                                                        question.data.label,
                                                        companyLanguage,
                                                    ),
                                                    employee: previewEvaluee,
                                                })}
                                            />
                                        </Text>
                                    </div>

                                    {question.data.status && (
                                        <div
                                            onClick={stopTapPropagation}
                                            onPointerDown={stopTapPropagation}
                                        >
                                            <Pills
                                                variant={
                                                    question.data.status ===
                                                    ReviewTemplateStatus.ACTIVE
                                                        ? 'activeLight'
                                                        : 'inactiveLight'
                                                }
                                                size={'sm'}
                                                status={
                                                    question.data.status ===
                                                    ReviewTemplateStatus.ACTIVE
                                                        ? 'active'
                                                        : 'inactive'
                                                }
                                            >
                                                {question.data.status ===
                                                ReviewTemplateStatus.ACTIVE
                                                    ? t('common.active')
                                                    : t('common.inactive')}
                                            </Pills>
                                        </div>
                                    )}

                                    <div
                                        onClick={stopTapPropagation}
                                        onPointerDown={stopTapPropagation}
                                    >
                                        <ActionMenu
                                            items={getActionMenuItems(question.data)}
                                            size={'sm'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                }}
            />
        </div>
    );
};
